import { Box, Card, Container, Link, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import Page from "src/components/Page";
import useGetLogo from "src/components/logo/hooks/use-logo";
import Translate from "src/components/translate";
import { DOMAIN_NAME } from "src/config";
import LanguagePopover from "src/layouts/shared/header/language-popover";
import { PATH_AUTH } from "src/routes/paths";
import LoginForm from "./components/MainSection/login";
import Header from "./components/Header";

const Login = () => {
  const logo = useGetLogo();
  const isSubAdmin = localStorage.getItem("isSubAdmin") === "true";

  if (isSubAdmin) {
    const menu_lists = JSON.parse(localStorage.getItem("menu"));
    const [menu] = menu_lists;
    const { path, children } = menu?.items[0] || {};

    if (path.includes("dashboard")) {
      window.location = children[0]?.path;
      return;
    }
    window.location = path;
    return;
  }

  return (
    <Page
      title="Login"
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? { xs: "#121820", sm: "#0b1016" }
            : { xs: "#ffffff", sm: "#f4f6f9" },
        backgroundImage: (theme) =>
          theme.palette.mode === "dark"
            ? {
                xs: "none",
                sm: "radial-gradient(ellipse at 50% -10%, rgba(16, 45, 102, 0.3) 0%, transparent 60%), radial-gradient(circle at 90% 90%, rgba(37, 211, 102, 0.1) 0%, transparent 40%)",
              }
            : {
                xs: "none",
                sm: "radial-gradient(ellipse at 50% -10%, rgba(10, 29, 59, 0.06) 0%, transparent 60%), radial-gradient(circle at 90% 90%, rgba(37, 211, 102, 0.05) 0%, transparent 40%), linear-gradient(180deg, #F8FAFC 0%, #EEF2F6 100%)",
              },
      }}
    >
      <Header />

      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          my: { xs: 0, sm: "auto" },
          py: { xs: 0, sm: 6 },
          px: { xs: 0, sm: 2 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: "490px" },
            borderRadius: { xs: 0, sm: "24px" },
            px: { xs: 2.5, sm: 4.5 },
            py: { xs: 3.5, sm: 4.5 },
            minHeight: { xs: "100vh", sm: "auto" },
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? {
                    xs: "none",
                    sm: "0 24px 60px -12px rgba(0, 0, 0, 0.8), 0 0 1px 1px rgba(255, 255, 255, 0.08)",
                  }
                : {
                    xs: "none",
                    sm: "0 20px 50px -12px rgba(10, 29, 59, 0.12), 0 1px 3px rgba(0, 0, 0, 0.04)",
                  },
            border: (theme) =>
              theme.palette.mode === "dark"
                ? {
                    xs: "none",
                    sm: "1px solid rgba(255, 255, 255, 0.08)",
                  }
                : {
                    xs: "none",
                    sm: "1px solid rgba(0, 0, 0, 0.06)",
                  },
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#121820" : "#ffffff",
            backdropFilter: { sm: "blur(20px)" },
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* Logo Badge */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              component="a"
              href="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1.25,
                borderRadius: "18px",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "#ffffff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.06)",
                transition: "transform 0.2s ease-in-out",
                "&:hover": { transform: "scale(1.03)" },
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{ height: 46, width: "auto" }}
              />
            </Box>
          </Box>

          {/* Title & Subtitle */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.5px",
              fontSize: { xs: "1.65rem", sm: "1.9rem" },
              color: "text.primary",
              mb: 0.5,
            }}
          >
            <Translate>register.welcome</Translate>
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3.5,
              fontSize: "0.9rem",
            }}
          >
            <Translate>register.sign_to</Translate>{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {DOMAIN_NAME}
            </Box>
          </Typography>

          {/* Form */}
          <LoginForm />

          {/* Footer (Language & Don't have an account) */}
          <Box
            sx={{
              mt: 3.5,
              pt: 2.25,
              borderTop: (theme) =>
                `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LanguagePopover />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "0.85rem" }}
                >
                  <Translate>register.don't_have</Translate>
                </Typography>
                <Link
                  component={RouterLink}
                  to={PATH_AUTH.register}
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Translate>register.get_started</Translate> →
                </Link>
              </Box>
            </Stack>
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Login;

